import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, IconButton, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Transition } from "../../../UiComponents/Transition/Transition";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
// The Company Wallet dialogs' own look (navy header band, bordered inputs, footer), like the Users page dialog.
import Styles from "../../ClientCompanyWallet/ClientCompanyWallet.module.scss";
import { CHOICE_TOGGLE_SX } from "../../Policy/OnlinePolicy/components/formStyles";
import { createAttemptTracker, sanitizeAmountInput, shouldReuseRequestId } from "../../AddMoneyToUser/addMoneyUtils";
import { addRoadzenWalletMoney } from "../../../Service/RoadzenService";
import { GetClient } from "../../../Service/_index";
import { MODES, NOTE_MAX, agencyOptionsOf, amountProblem, isSafeLink, rupeesText, zenProblem } from "./roadzenWalletUtils";

const buttonSx = { textTransform: "none", borderRadius: "6px" };

/**
 * Add Amount for the ACTIVE RoadZen user: Test credit (sandbox only: RoadZen's payment link does not work there) or a
 * Payment link. A retry of the same amount keeps its request id, so a lost answer never credits twice
 * (AddMoneyToUser/addMoneyUtils.js createAttemptTracker). After a link is made it is shown with Copy and Open (never
 * opened by itself); after a test credit the balance before and after is shown. `onDone` re-reads the page.
 * Zen Wallet (2026-11-04): once RoadZen confirms the money, the same amount goes to an agency's Zen Wallet — the adder's
 * own; with `chooseAgency` (the super admin) an Agency must be picked.
 */
const AddAmountDialog = ({ open, user, testCreditAvailable, chooseAgency = false, onClose, onDone }) => {
  const [mode, setMode] = useState(testCreditAvailable ? "TEST_CREDIT" : "PAYMENT_LINK");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [result, setResult] = useState(null); // { mode, message, topup?, balanceBeforePaise?, balanceAfterPaise?, zen? }
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState("");
  const tracker = useRef(createAttemptTracker());

  useEffect(() => {
    if (!open || !chooseAgency) return undefined;
    let alive = true;
    Promise.resolve(GetClient())
      .then((res) => {
        if (alive) setAgencies(agencyOptionsOf(res && res.data));
      })
      .catch(() => {
        if (alive) setAgencies([]);
      });
    return () => {
      alive = false;
    };
  }, [open, chooseAgency]);

  useEffect(() => {
    if (!open) return;
    setMode(testCreditAvailable ? "TEST_CREDIT" : "PAYMENT_LINK");
    setAmount("");
    setNote("");
    setTouched(false);
    setProblem("");
    setResult(null);
    setAgencyId("");
    tracker.current.reset();
  }, [open, testCreditAvailable]);

  if (!open || !user) return null;

  const amountError = amountProblem(mode, amount);
  const noteError = note.trim().length > NOTE_MAX ? `The note can be ${NOTE_MAX} letters at most` : "";
  const agencyError = chooseAgency && !agencyId ? "Choose the agency whose Zen Wallet gets this money" : "";
  const picked = agencies.find((a) => a.value === agencyId);
  const whose = !chooseAgency ? "your" : picked ? `${picked.label}'s` : "the chosen agency's";
  const zenHint = `${mode === "TEST_CREDIT" ? "When RoadZen confirms it" : "When RoadZen says the link is paid"}, the same amount goes to ${whose} Zen Wallet`;

  const submit = async () => {
    setTouched(true);
    if (amountError || noteError || agencyError || busy) return;
    setBusy(true);
    setProblem("");
    const body = { mode, amount: amount.replace(/\.$/, ""), requestId: tracker.current.idFor({ userId: user.id, amount, mode, agencyId }) };
    if (chooseAgency) body.adminClientId = agencyId;
    if (mode === "PAYMENT_LINK" && note.trim()) body.note = note.trim();
    const res = await addRoadzenWalletMoney(user.id, body);
    setBusy(false);
    // no answer, or still running: the same click may be sent again with the same id (never a second credit)
    if (!shouldReuseRequestId(res)) tracker.current.reset();
    onDone(res);
    if (res.error) {
      ToastError(res.message);
      setProblem(res.message);
      return;
    }
    ToastSuccess(res.message);
    setResult({ mode, message: res.message, ...(res.data || {}) });
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      ToastSuccess("Payment link copied");
    } catch (e) {
      ToastError("Could not copy. Select the link and copy it");
    }
  };

  const link = result && result.topup && isSafeLink(result.topup.paymentUrl) ? result.topup.paymentUrl : null;

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      TransitionComponent={Transition}
      keepMounted={false}
      PaperProps={{ sx: { maxWidth: "500px !important", width: "100%", borderRadius: "10px" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between" }} className={Styles.DialogHeader}>
        <Box>
          <Typography className={Styles.DialogTitle}>Add Amount</Typography>
          <Typography className={Styles.DialogSubtitle}>
            {user.name} · {user.code} · RoadZen wallet
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "white" }} disabled={busy} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} className={Styles.DialogBody}>
        {result ? (
          <>
            {/* RoadZen took the money but the Zen Wallet did not get it: a warning, never a plain success */}
            <Alert severity={zenProblem(result.zen) ? "warning" : "success"}>{result.message}</Alert>
            {result.mode === "TEST_CREDIT" && typeof result.balanceAfterPaise === "number" && (
              <Typography sx={{ fontSize: 13, color: "#344054" }}>
                Balance {rupeesText(result.balanceBeforePaise)} → <b>{rupeesText(result.balanceAfterPaise)}</b>
              </Typography>
            )}
            {link && (
              <Box sx={{ border: "1px solid #EAECF0", borderRadius: "8px", p: 1.5, backgroundColor: "#F9FAFB" }}>
                <Typography className={Styles.FieldLabel}>Payment link</Typography>
                <Typography sx={{ fontSize: 13, color: "#101828", overflowWrap: "anywhere", mb: 1 }} data-testid="roadzen-payment-link">
                  {link}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button variant="outlined" size="small" sx={buttonSx} startIcon={<ContentCopyRoundedIcon />} onClick={() => copy(link)}>
                    Copy
                  </Button>
                  <Button variant="outlined" size="small" sx={buttonSx} startIcon={<OpenInNewRoundedIcon />} onClick={() => window.open(link, "_blank", "noopener,noreferrer")}>
                    Open
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box>
              <Typography className={Styles.FieldLabel}>How</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                aria-label="Test credit or payment link"
                onChange={(e, next) => {
                  if (next) {
                    setMode(next);
                    setProblem("");
                  }
                }}
                sx={CHOICE_TOGGLE_SX}
              >
                {testCreditAvailable && <ToggleButton value="TEST_CREDIT">Test credit</ToggleButton>}
                <ToggleButton value="PAYMENT_LINK">Payment link</ToggleButton>
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.75 }}>{MODES[mode].hint}</Typography>
              <Typography data-testid="roadzen-zen-hint" sx={{ fontSize: 12, color: "#475467", mt: 0.5 }}>
                {zenHint}
              </Typography>
            </Box>
            {problem && (
              <Alert severity="error" sx={{ py: 0 }}>
                {problem}
              </Alert>
            )}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography component="label" htmlFor="roadzen-amount" className={Styles.FieldLabel}>
                Amount (₹)<span style={{ color: "#B42318" }}> *</span>
              </Typography>
              <input
                id="roadzen-amount"
                className={Styles.InputFiled}
                inputMode="decimal"
                autoFocus
                placeholder={`${rupeesText(MODES[mode].minPaise)} to ${rupeesText(MODES[mode].maxPaise)}`}
                value={amount}
                onChange={(e) => setAmount(sanitizeAmountInput(e.target.value))}
                onBlur={() => setTouched(true)}
                disabled={busy}
              />
              {touched && amountError ? (
                <Typography className={Styles.FieldError}>{amountError}</Typography>
              ) : (
                <Typography sx={{ fontSize: 11, color: "#667085", mt: "3px" }}>
                  {rupeesText(MODES[mode].minPaise)} to {rupeesText(MODES[mode].maxPaise)}
                </Typography>
              )}
            </Box>
            {chooseAgency && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography component="label" htmlFor="roadzen-agency" className={Styles.FieldLabel}>
                  Agency (gets it in its Zen Wallet)<span style={{ color: "#B42318" }}> *</span>
                </Typography>
                <select id="roadzen-agency" className={Styles.InputFiled} style={{ backgroundColor: "#fff" }} value={agencyId} onChange={(e) => setAgencyId(e.target.value)} onBlur={() => setTouched(true)} disabled={busy}>
                  <option value="">Choose an agency</option>
                  {agencies.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
                {touched && agencyError && <Typography className={Styles.FieldError}>{agencyError}</Typography>}
              </Box>
            )}
            {mode === "PAYMENT_LINK" && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography component="label" htmlFor="roadzen-note" className={Styles.FieldLabel}>
                  Note (shown on the payment page)
                </Typography>
                <input id="roadzen-note" className={Styles.InputFiled} maxLength={NOTE_MAX} placeholder="e.g. November float" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
                {noteError && <Typography className={Styles.FieldError}>{noteError}</Typography>}
              </Box>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "10px" }} className={Styles.DialogFooter}>
        {result ? (
          <Button variant="contained" disableElevation className={Styles.TopupButton} onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="outlined" sx={buttonSx} onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              className={Styles.TopupButton}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <AddCardRoundedIcon sx={{ fontSize: "16px !important" }} />}
              onClick={submit}
              disabled={busy}
            >
              {busy ? "Adding" : mode === "TEST_CREDIT" ? "Add Test Credit" : "Make Payment Link"}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default AddAmountDialog;
